import React, { Component } from "react";
import { Col, Row, Spinner } from "reactstrap";
import { RouteComponentProps } from "react-router";
import {
  Avatar,
  TableCell,
  TableRow,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  BanksService,
  CountryMetric,
  User,
  UserBank,
  UserBankStatus,
  UsersAdminService,
  UserService,
} from "../../../generated";
import { BanksList } from "../../user/banks/List/BanksList";
import { HourglassEmpty, VerifiedUser } from "@mui/icons-material";
import Flag from "react-world-flags";
import { FaPen } from "react-icons/fa";
import { SelectedBanksList } from "../../user/banks/Selected/SelectedBanks";
import { CountryList } from "../../user/banks/CountryList/CountryList";
import { EditUserBankModal } from "../../user/banks/EditUserBankModal";
import { UserInformation } from "../../user/UserInformation";
import { EditUserModal } from "../../user/EditUserModal";
import { TesterType } from "../../../generated/models/TesterType";

export interface Props extends RouteComponentProps<{ userEmail: string }> {
  isAdminMode: boolean;
}

export interface State {
  selectedCountryCode: string;
  countries: CountryMetric[];
  countriesLoading: boolean;
  banks: UserBank[];
  banksLoading: boolean;
  banksLoaded: boolean;
  selectedBanks: UserBank[];
  selectedBanksLoading: boolean;
  snackBarOpen: boolean;
  snackBarAlertSeverity: string;
  snackBarAlertMessage: string;
  userEmail: string;
  user: User;
  userLoading: boolean;
  userNotFound: boolean;
  editedBank: UserBank;
  editedUser: User;
}

export class UserProfile extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      selectedCountryCode: "DK",
      countries: [],
      countriesLoading: true,
      banks: [],
      banksLoading: false,
      banksLoaded: false,
      selectedBanks: [],
      selectedBanksLoading: false,
      snackBarOpen: false,
      snackBarAlertSeverity: "",
      snackBarAlertMessage: "",
      userEmail: this.props.match.params.userEmail,
      user: null,
      userLoading: false,
      userNotFound: false,
      editedBank: null,
      editedUser: null,
    };

    this.saveBank = this.saveBank.bind(this);
    this.saveUser = this.saveUser.bind(this);
  }

  async componentDidMount() {
    await this.getUserDetails();
    await this.getSelectedBanksOnly();
  }
  async componentDidUpdate(
    prevProps: Readonly<Props>,
    prevState: Readonly<State>
  ) {
    if (
      prevState.user !== this.state.user &&
      this.state.user !== null &&
      this.state.user !== undefined
    ) {
      await this.getCountries();
      await this.getBanks(this.state.selectedCountryCode);
    }
  }

  async getUserDetails() {
    try {
      if (this.props.isAdminMode) {
        const user = await UsersAdminService.usersAdminGetUser({
          email: this.state.userEmail,
        });

        this.setState({
          user: user,
          userNotFound: false,
        });
      } else {
        const user = await UserService.userGetUser();

        this.setState({
          user: user.user,
          userNotFound: false,
        });
      }
    } catch {
      this.setState({
        userNotFound: true,
      });
    }
  }
  async getBanks(countryCode: string) {
    this.setState({
      selectedCountryCode: countryCode,
      banksLoading: true,
      banksLoaded: false,
    });

    const allBanks = await BanksService.banksGetList({
      countryCode,
    });

    this.setState({
      banksLoaded: true,
      banksLoading: false,
      banks: allBanks,
    });
  }
  async getSelectedBanksOnly() {
    this.setState({
      selectedBanksLoading: true,
    });

    const result = this.props.isAdminMode
      ? await UsersAdminService.banksGetSelected({
          email: this.state.userEmail,
        })
      : await BanksService.banksGetSelected({ email: this.state.user.email });

    this.setState({
      selectedBanksLoading: false,
      selectedBanks: result,
    });
  }
  async getCountries() {
    this.setState({
      countriesLoading: true,
    });
    const result = this.props.isAdminMode
      ? await UsersAdminService.usersAdminGetCountries()
      : await BanksService.banksGetCountries();

    this.setState({
      countriesLoading: false,
      countries: result.sort((a, b) => a.name.localeCompare(b.name)),
    });
  }

  renderTableBody(userBank: UserBank[]) {
    return (
      <>
        {userBank.length > 0
          ? this.renderRows(userBank)
          : this.renderEmptyRow()}
      </>
    );
  }
  renderEmptyRow() {
    return (
      <TableRow>
        <TableCell colSpan={8} align="center">
          Nothing here
        </TableCell>
      </TableRow>
    );
  }
  renderRows(userBank: UserBank[]) {
    return userBank.map((userBank) => (
      <TableRow key={userBank.rowKey}>
        <TableCell>
          {
            <Flag
              code={userBank.countryCode}
              alt={userBank.countryCode}
              width="35px"
              height="25px"
            />
          }
        </TableCell>
        <TableCell>{userBank.displayName ?? userBank.legalName}</TableCell>
        <TableCell>{userBank.status ?? "Available"}</TableCell>
        <TableCell style={{ width: "1000px" }}>
          {userBank.status === UserBankStatus.AVAILABLE ||
          userBank.status === undefined ||
          userBank.status === null ? (
            <Tooltip title="bank available">
              <VerifiedUser htmlColor="green" />
            </Tooltip>
          ) : (
            <Tooltip title="provider in progress">
              <HourglassEmpty />
            </Tooltip>
          )}
        </TableCell>
        <TableCell>
          <Tooltip title="Mange user's banks">
            <IconButton
              onClick={() => this.handleBanksListClick(this.state.user)}
              color="primary"
            >
              <FaPen size="16px" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    ));
  }

  handleBanksListClick = (user: User) => {
    this.props.history.push(`/admin/users/banks/` + user.email);
  };
  handleCountryListClick = async (countryCode: string) => {
    await this.getBanks(countryCode);
  };
  handleSelectBankClick = async (bank: UserBank) => {
    this.setState({
      selectedBanksLoading: true,
    });

    try {
      this.props.isAdminMode
        ? await UsersAdminService.usersAdminSelectBankForUser({
            bank,
            email: this.state.userEmail,
          })
        : await BanksService.banksSelectBank({
            bank,
            email: this.state.userEmail,
          });

      this.handleOpenSnackBar(
        (bank.displayName ?? bank.legalName) + " added",
        "success"
      );

      const selectedBanks = this.props.isAdminMode
        ? await UsersAdminService.banksGetSelected({
            email: this.state.userEmail,
          })
        : await BanksService.banksGetSelected({
            email: this.state.user.email,
          });

      this.setState({
        selectedBanks: selectedBanks,
        selectedBanksLoading: false,
      });
    } catch {
      this.handleOpenSnackBar("Something went wrong", "danger");
    }
  };
  handleUnselectBankClick = async (bank: UserBank) => {
    this.setState({
      banksLoading: true,
      selectedBanksLoading: true,
    });

    try {
      if (this.props.isAdminMode) {
        UsersAdminService.banksRemoveSelection({
          unselectedBank: bank,
          email: this.state.userEmail,
        });
      } else {
        await BanksService.banksRemoveSelection({
          unselectedBank: bank,
          email: this.state.userEmail,
        });
      }

      this.handleOpenSnackBar(
        (bank.displayName ?? bank.legalName) + " unselected",
        "success"
      );
      this.unselectBank(bank);
    } catch {
      this.handleOpenSnackBar("Something went wrong", "danger");
    }

    this.setState({
      banksLoading: false,
      selectedBanksLoading: false,
    });
  };

  unselectBank(bank: UserBank) {
    let bankToEdit = bank;
    bankToEdit.selected = false;

    let editedBanks = [...this.state.banks];
    let index = editedBanks.findIndex(
      (b) => b.providerId === bank.providerId && b.id === bank.id
    );
    editedBanks[index] = bankToEdit;

    let editedSelectedBanks = [...this.state.selectedBanks];
    let indexSelectedBanks = editedSelectedBanks.findIndex(
      (b) => b.providerId === bank.providerId && b.id === bank.id
    );
    editedSelectedBanks.splice(indexSelectedBanks, 1);

    this.setState({
      selectedBanks: editedSelectedBanks,
      banks: editedBanks,
    });
  }

  handleOpenSnackBar(alertMessage: string, alertSeverity: string) {
    this.setState({
      snackBarAlertSeverity: alertSeverity,
      snackBarAlertMessage: alertMessage,
      snackBarOpen: true,
    });
  }
  handleCloseSnackBar = () => {
    this.setState({
      snackBarOpen: false,
    });
  };

  editBank(bank: UserBank) {
    bank.iban ??= "";
    bank.bban ??= "";
    this.setState({ editedBank: bank });
  }

  editUser(user: User) {
    user.paymentChannel ??= "";
    this.setState({ editedUser: user });
  }

  async saveBank(bank: UserBank) {
    this.props.isAdminMode
      ? await UsersAdminService.usersAdminEditBankForUser({
          bank: bank,
          email: this.state.userEmail,
        })
      : await BanksService.banksEditBank({
          editedBank: bank,
        });

    this.setState({
      selectedBanksLoading: true,
    });

    const selectedBanks = this.props.isAdminMode
      ? await UsersAdminService.banksGetSelected({
          email: this.state.userEmail,
        })
      : await BanksService.banksGetSelected({
          email: this.state.user.email,
        });

    this.setState({
      selectedBanksLoading: false,
      editedBank: null,
      selectedBanks,
    });
  }

  ResetPaymentSection(user: User) {
    if (user.testerType !== TesterType.EXTERNAL_TESTER) {
      user.hourlyRate = 0;
      user.paymentPerBank = 0;
      user.paymentChannel = "";
    }
    return user;
  }

  async saveUser(user: User) {
    user = this.ResetPaymentSection(user);

    const result = await UsersAdminService.usersAdminEditUser({
      user: user,
    });

    if (result.success) {
      this.setState({
        userLoading: true,
      });

      const editedUser = await UsersAdminService.usersAdminGetUser({
        email: user.email,
      });

      if (editedUser) {
        this.setState({
          userLoading: false,
          user: editedUser,
          editedUser: null,
        });
      }
    } else {
      this.setState({
        userLoading: false,
        userNotFound: true,
      });
    }

    this.setState({
      userLoading: false,
      editedUser: null,
    });
  }

  render() {
    return this.state.userNotFound === true ? (
      <div>
        <h3>User not found</h3>
        <span
          className="btn border px-4 btn-primary mt-5 float-right"
          onClick={() => this.props.history.goBack()}
        >
          Back
        </span>
      </div>
    ) : this.state.user === null ? (
      <Spinner />
    ) : (
      <div className="border rounded rounded-more overflow-hidden p-5">
        <div style={{ display: "flex", flexDirection: "row" }}>
          <Avatar
            className="float-right rounded rounded-circle"
            style={{ width: "60px", height: "60px" }}
            alt={this.state.user.firstName + " " + this.state.user.lastName}
            src={this.state.user.imageUrl}
          />
          <h3
            className="mb-1"
            style={{ marginLeft: "20px", marginTop: "10px" }}
          >
            {this.state.user.firstName || this.state.user.lastName ? (
              this.state.user.firstName + " " + this.state.user.lastName
            ) : (
              <span>---</span>
            )}
          </h3>
        </div>
        <br></br>
        <Row>
          <div className="border rounded rounded-more overflow-hidden p-5 mt-4 mt-lg-0 mb-5">
            <h3>{"All user's banks"}</h3>
            {this.state.selectedBanksLoading ? (
              <Spinner />
            ) : (
              <>
                <SelectedBanksList
                  onDelete={this.handleUnselectBankClick}
                  onEdit={(bank) => this.editBank(bank)}
                  banks={this.state.selectedBanks}
                />
                <EditUserBankModal
                  bank={this.state.editedBank}
                  close={() => this.setState({ editedBank: null })}
                  onClosed={() => this.setState({ editedBank: null })}
                  onSubmit={this.saveBank}
                  isAdminMode={this.props.isAdminMode}
                />
              </>
            )}
          </div>
        </Row>
        <Row>
          <Col lg={6}>
            <div className="border rounded rounded-more overflow-hidden p-5">
              <h2 className="mb-3">Add bank</h2>
              <h6>Select country:</h6>
              {this.state.countriesLoading ? (
                <Spinner />
              ) : (
                <CountryList
                  countries={this.state.countries}
                  selectedElement={this.state.selectedCountryCode}
                  handleListItemClick={this.handleCountryListClick}
                />
              )}
              {this.state.banksLoading ? (
                <Spinner />
              ) : (
                <BanksList
                  banks={this.state.banks}
                  onClick={this.handleSelectBankClick}
                  hidden={!this.state.banksLoaded}
                />
              )}
            </div>
          </Col>
          <Col lg={6}>
            {this.state.userLoading ? (
              <Spinner />
            ) : (
              <>
                <UserInformation
                  onEdit={(user) => this.editUser(user)}
                  user={this.state.user}
                  allowEdit={this.props.isAdminMode}
                />
                <EditUserModal
                  user={this.state.editedUser}
                  close={() => this.setState({ editedUser: null })}
                  onClosed={() => this.setState({ editedUser: null })}
                  onSubmit={this.saveUser}
                />
              </>
            )}
          </Col>
        </Row>
      </div>
    );
  }
}
